/**
 * useAssignmentsData - Manages assignments data
 *
 * This hook manages the list of assignments and individual assignment details.
 * Used by AssignmentsDashboardContainer and AssignmentDetailContainer.
 *
 * Follows the URL-as-source-of-truth pattern established by useSubjectsData:
 * - Auto-fetches the whole grade's assignments when urlGradeCode changes
 * - Auto-fetches detail when urlAssignmentId changes
 * - Caches list by grade, detail by assignment ID
 * - Supports invalidateAssignmentsCache for forced refetch
 *
 * The list is fetched once per grade (all terms). Per-term views are
 * derived client-side from this single list, so the term counts shown on
 * the dashboard/term/sidebar always match the assignments the list serves.
 */

import { useState, useCallback, useEffect, useRef, useReducer } from "react";
import { assignmentsApi } from "@/api/assignmentsApi";
import {
  mapAssignmentList,
  mapAssignmentDetail
} from "@utils/assignmentsMapper";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type {
  Assignment,
  AssignmentDetail
} from "@ts/interfaces/assignments/assignmentsInterface";
import type {
  AssignmentTermSummary,
  AssignmentGradeSummary
} from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import { logger } from "@/utils/loggingUtils";

interface UseAssignmentsDataParams {
  urlGradeCode: number | undefined;
  urlTermNumber: number | undefined;
  urlAssignmentId: string | undefined;
  /**
   * When false the hook stays at initial IDLE state and makes no API calls.
   * Follows the React Query `enabled` pattern — the caller controls when
   * fetching is allowed (e.g. feature-flag + assessed-grade gate).
   * Defaults to true for backwards compatibility.
   */
  enabled?: boolean;
}

export interface AssignmentsDataState {
  // List data
  assignments: Assignment[];
  /** Per-term assignment counts, keyed by term number. Feeds the merged Term view. */
  termSummary: Record<string, AssignmentTermSummary>;
  /** Grade-wide submission stats. Feeds the GradeProgress panel. */
  gradeSummary: AssignmentGradeSummary | null;
  listStatus: REQUEST_STATUS;
  listError: ErrorInterface | null;

  // Detail data
  assignmentDetail: AssignmentDetail | null;
  detailForId: string | undefined;
  detailStatus: REQUEST_STATUS;
  detailError: ErrorInterface | null;
}

export interface AssignmentsDataActions {
  invalidateAssignmentsCache: () => void;
  invalidateDetailCache: () => void;
  refetchDetail: () => Promise<AssignmentDetail | null>;
}

export type AssignmentsData = AssignmentsDataState & AssignmentsDataActions;

export function useAssignmentsData({
  urlGradeCode,
  urlTermNumber,
  urlAssignmentId,
  enabled = true
}: UseAssignmentsDataParams): AssignmentsData {
  // List state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [termSummary, setTermSummary] = useState<
    Record<string, AssignmentTermSummary>
  >({});
  const [gradeSummary, setGradeSummary] =
    useState<AssignmentGradeSummary | null>(null);
  const [listStatus, setListStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [listError, setListError] = useState<ErrorInterface | null>(null);

  // Detail state
  const [assignmentDetail, setAssignmentDetail] =
    useState<AssignmentDetail | null>(null);
  const [detailStatus, setDetailStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [detailError, setDetailError] = useState<ErrorInterface | null>(null);

  // Cache tracking via refs (same pattern as useSubjectsData)
  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  const lastFetchedDetailIdRef = useRef<string | undefined>(undefined);

  // Invalidation counters — incrementing forces the useEffect to re-run
  const [listInvalidationCount, bumpListInvalidation] = useReducer(
    (c: number) => c + 1,
    0
  );
  const [detailInvalidationCount, bumpDetailInvalidation] = useReducer(
    (c: number) => c + 1,
    0
  );

  // Guard: only fetch detail when the URL param looks like a real UUID.
  // React Router may match "term-1" as :assignmentId when the
  // assignments/term/:termId route could collide with assignments/:assignmentId.
  const isValidAssignmentId =
    !!urlAssignmentId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      urlAssignmentId
    );

  // Invalidate list cache so next render cycle will refetch
  const invalidateAssignmentsCache = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    bumpListInvalidation();
  }, []);

  // Invalidate detail cache so next render cycle will refetch
  const invalidateDetailCache = useCallback(() => {
    lastFetchedDetailIdRef.current = undefined;
    bumpDetailInvalidation();
  }, []);

  // Imperatively refetch the current assignment detail and return the fresh
  // result. Used to renew an expired signed URL on click. Returns null when
  // there is no current assignment to fetch.
  const refetchDetail =
    useCallback(async (): Promise<AssignmentDetail | null> => {
      if (!isValidAssignmentId || !urlGradeCode || !urlTermNumber) return null;

      const response = await assignmentsApi.getAssignmentDetail(
        urlAssignmentId,
        urlGradeCode,
        urlTermNumber
      );
      const detail = mapAssignmentDetail(response);
      setAssignmentDetail(detail);
      setDetailStatus(REQUEST_STATUS.SUCCESS);
      return detail;
    }, [isValidAssignmentId, urlAssignmentId, urlGradeCode, urlTermNumber]);

  // ── Auto-fetch the whole grade's assignments when grade changes ──
  useEffect(() => {
    if (!enabled || !urlGradeCode) {
      setAssignments([]);
      setTermSummary({});
      setGradeSummary(null);
      setListStatus(REQUEST_STATUS.IDLE);
      setListError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Already fetched for this grade during the current cache window.
    if (lastFetchedGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    setListStatus(REQUEST_STATUS.PENDING);
    setListError(null);

    assignmentsApi
      .getAssignmentsList(urlGradeCode)
      .then(response => {
        // Guard against stale responses when the grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        setAssignments(mapAssignmentList(response.data ?? []));
        setTermSummary(response.term_summary ?? {});
        setGradeSummary(response.grade_summary ?? null);
        setListStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when the grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        logger.warn("Error fetching assignments list:", error);
        setListStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setListError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch assignments"
        });
      });
  }, [enabled, urlGradeCode, listInvalidationCount]);

  // ── Auto-fetch detail when assignment ID changes ────────────
  useEffect(() => {
    if (!enabled || !isValidAssignmentId || !urlGradeCode || !urlTermNumber) {
      setAssignmentDetail(null);
      setDetailStatus(REQUEST_STATUS.IDLE);
      setDetailError(null);
      lastFetchedDetailIdRef.current = undefined;
      return;
    }

    // Already fetched for this assignment ID.
    if (lastFetchedDetailIdRef.current === urlAssignmentId) {
      return;
    }

    lastFetchedDetailIdRef.current = urlAssignmentId;
    setDetailStatus(REQUEST_STATUS.PENDING);
    setDetailError(null);

    assignmentsApi
      .getAssignmentDetail(urlAssignmentId, urlGradeCode, urlTermNumber)
      .then(response => {
        // Guard against stale responses
        if (lastFetchedDetailIdRef.current !== urlAssignmentId) return;
        setAssignmentDetail(mapAssignmentDetail(response));
        setDetailStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses
        if (lastFetchedDetailIdRef.current !== urlAssignmentId) return;
        logger.warn("Error fetching assignment detail:", error);
        setDetailStatus(REQUEST_STATUS.ERROR);
        lastFetchedDetailIdRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setDetailError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch assignment detail"
        });
      });
  }, [enabled, urlAssignmentId, urlGradeCode, detailInvalidationCount]);

  return {
    // List state
    assignments,
    termSummary,
    gradeSummary,
    listStatus,
    listError,

    // Detail state
    assignmentDetail,
    detailForId: lastFetchedDetailIdRef.current,
    detailStatus,
    detailError,

    // Actions
    invalidateAssignmentsCache,
    invalidateDetailCache,
    refetchDetail
  };
}
